import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle, XCircle, CircleNotch } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { resolveSessionPaymentView, type SessionPaymentState } from "@/lib/payment-result-view";
import type { SessionReturn } from "@/lib/payment-return-url";
import { useConfirmMonriReturn } from "@/hooks/useConfirmMonriReturn";

type PaymentStatusResp = SessionPaymentState;

const POLL_MS = 2000;
const POLL_WINDOW_MS = 30000;

/**
 * Result page after the Monri form for a single session. Rendered by both the
 * path-based return route and the legacy query-based one; the props are
 * already validated (see src/lib/payment-return-url.ts).
 */
export function SessionPaymentResult({ outcome: status, sessionId: session, holdToken: token }: SessionReturn) {
  const { t } = useTranslation();
  const [startedAt] = useState(() => Date.now());
  const [pollWindowOver, setPollWindowOver] = useState(false);
  const queryClient = useQueryClient();
  useEffect(() => {
    const id = setTimeout(() => setPollWindowOver(true), POLL_WINDOW_MS);
    return () => clearTimeout(id);
  }, []);

  // Poll DB truth (webhook result) rather than trusting the outcome in the return URL.
  const { data } = useQuery<PaymentStatusResp>({
    queryKey: ["payment-status", session],
    queryFn: async () => {
      const res = await fetch(`/api/sessions/${session}/payment-status`);
      if (!res.ok) throw new Error("payment-status");
      return res.json() as Promise<PaymentStatusResp>;
    },
    enabled: !!session,
    retry: false,
    refetchInterval: (query) => {
      const d = query.state.data;
      if (d && (d.paymentStatus === "paid" || d.paymentStatus === "failed" || d.status === "cancelled" || d.refundPending)) {
        return false;
      }
      if (Date.now() - startedAt > POLL_WINDOW_MS) return false;
      return POLL_MS;
    },
  });

  // Monri signs the success redirect; confirming it covers a lost callback.
  useConfirmMonriReturn(status === "success", () =>
    queryClient.invalidateQueries({ queryKey: ["payment-status", session] }),
  );

  // Monri never calls back for a cancelled or declined attempt, so coming back
  // through cancel_url is the only signal. Release the unpaid session once so
  // the slot is free again and "try again" really works. Holds (token) stay
  // open on purpose; the server refuses anything that is not an unpaid card
  // booking of this client.
  const [releaseSent, setReleaseSent] = useState(false);
  useEffect(() => {
    if (releaseSent || status !== "cancelled" || token || !session) return;
    if (!data || data.status !== "scheduled" || data.paymentStatus !== "pending") return;
    setReleaseSent(true);
    fetch(`/api/sessions/${session}/abandon-payment`, { method: "POST" })
      .then(() => queryClient.invalidateQueries({ queryKey: ["payment-status", session] }))
      .catch(() => {});
  }, [releaseSent, status, token, session, data, queryClient]);

  const view = resolveSessionPaymentView(data, status, pollWindowOver);

  const tone =
    view === "success" ? "success" : view === "processing" ? "processing" : "error";
  const bg =
    tone === "success"
      ? "oklch(92% 0.05 155)"
      : tone === "processing"
        ? "oklch(93% 0.03 250)"
        : "oklch(92% 0.05 25)";

  const title =
    view === "success"
      ? t("booking.paymentSuccess")
      : view === "processing"
        ? t("booking.paymentProcessing")
        : view === "cancelled"
          ? t("booking.paymentCancelledTitle")
          : view === "refundPending"
            ? t("booking.paymentRefundPendingTitle")
          : view === "holdRetry"
            ? status === "cancelled"
              ? t("booking.paymentHoldRetryTitle", { defaultValue: "Plaćanje nije uspjelo" })
              : t("booking.paymentHoldUnconfirmedTitle", { defaultValue: "Plaćanje nije potvrđeno" })
            : t("booking.paymentFailed");

  const desc =
    view === "success"
      ? t("booking.paymentSuccessDesc")
      : view === "processing"
        ? t("booking.paymentProcessingDesc")
        : view === "cancelled"
          ? t("booking.paymentCancelledDesc")
          : view === "refundPending"
            ? t("booking.paymentRefundPendingDesc")
          : view === "holdRetry"
            ? holdRetryDesc()
            : t("booking.paymentFailedDesc");

  // A declined card keeps the reservation open. Back from a cancel at the
  // gateway the payment certainly did not go through; when polling simply ran
  // out, the approval may still be on its way, so don't push a second payment.
  function holdRetryDesc(): string {
    if (status === "cancelled") {
      return token
        ? t("booking.paymentHoldRetryDesc", {
            defaultValue: "Rezervacija je i dalje aktivna, možete pokušati ponovo.",
          })
        : t("booking.paymentHoldRetryDescNoLink", {
            defaultValue:
              "Rezervacija je i dalje aktivna. Možete pokušati ponovo putem linka za plaćanje koji ste dobili e-mailom.",
          });
    }
    return token
      ? t("booking.paymentHoldUnconfirmedDesc", {
          defaultValue:
            "Plaćanje još nije potvrđeno. Ako je iznos skinut s kartice, potvrdu ćete dobiti e-mailom. Ako nije, rezervacija je i dalje aktivna i možete pokušati ponovo.",
        })
      : t("booking.paymentHoldUnconfirmedDescNoLink", {
          defaultValue:
            "Plaćanje još nije potvrđeno. Ako je iznos skinut s kartice, potvrdu ćete dobiti e-mailom. Ako nije, možete pokušati ponovo putem linka za plaćanje koji ste dobili e-mailom.",
        });
  }

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="max-w-sm text-center">
        <div
          className="mx-auto flex size-16 items-center justify-center rounded-full"
          style={{ background: bg }}
        >
          {view === "success" ? (
            <CheckCircle className="size-8" style={{ color: "oklch(55% 0.15 155)" }} />
          ) : view === "processing" ? (
            <CircleNotch className="size-8 animate-spin" style={{ color: "oklch(50% 0.13 250)" }} />
          ) : (
            <XCircle className="size-8" style={{ color: "oklch(55% 0.15 25)" }} />
          )}
        </div>
        <h2
          className="mt-5 text-xl font-bold text-foreground"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          {title}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">{desc}</p>

        <div className="mt-6 flex flex-wrap justify-center gap-2">
          {view === "holdRetry" && token && (
            <Link to="/booking/finalize/$token" params={{ token }}>
              <Button>{t("booking.paymentHoldRetryButton", { defaultValue: "Pokušaj ponovo" })}</Button>
            </Link>
          )}
          <Link to="/dashboard/client/sessions">
            <Button>{t("booking.myAppointments")}</Button>
          </Link>
          <Link to="/therapists">
            <Button variant="outline">{t("booking.backToBooking")}</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
